/**
 * orderResource — the Shopify order book, authored against the resource-kit v2
 * model/view contracts and consumed directly as a `ResourceBundle` by
 * `ResourcePage` / `ResourceDetailPage` (#1805 slice A4c).
 *
 * Orders got its own slice because its risk is concentrated rather than spread.
 * Four things here have no counterpart in the other A4 resources:
 *
 *   - EIGHT `fields[].render` callbacks, FOUR of which fork on the surface they
 *     render on. They are now named widgets in `./widgets.tsx`, which reads
 *     `WidgetProps.surface` — `fields[].render` has no v2 carrier, and it also
 *     SHORT-CIRCUITS the widget
 *     (`packages/resource-kit-ui/react/registry/export-text.ts::formatFieldTextForExport`), so the callbacks
 *     had to go, not merely be duplicated.
 *   - ONE low-frequency group, `more`, still carries `collapsible` /
 *     `defaultCollapsed`. It is the #1922 merge of the former `timeline` /
 *     `notes` / `meta` groups: under the declared #1918 tab display each of them became
 *     its own rail trigger while the folding the flags used to buy was ignored,
 *     so three rarely-opened groups cost three triggers. Ruling (#1922):
 *     low-frequency read-only fields share one tab. The flags are kept ON THE
 *     MERGED GROUP because they are the whole folding behaviour again the
 *     moment this resource declares `groupDisplay: 'card'`.
 *   - `detail.statusField` and `auditResourceType`, whose carriers are the host
 *     block (`packages/resource-kit-ui/react/resource/ResourceDetailPanel.tsx::ResourceDetailPanel`) and the model
 *     (`packages/resource-kit-ui/react/form/FormView.tsx::FormView`) respectively. Both are silent losses if dropped:
 *     a missing badge and an empty change-history tab both still render.
 *   - `total_price` is the repo's other `currency` column, and it declares NO
 *     `currencyField`. That is deliberate: the renderer's `'USD'` default is
 *     what this column has always used. The `currency` COLUMN never drove the
 *     symbol.
 *
 * `created_at` is declared on the MODEL and left out of the list view, and that
 * asymmetry is the point — the same one drivers has. The list sorts by
 * `created_at:desc` and always has, but `created_at` was never a column:
 * `ListViewDef.defaultSort.field` is keyed on the model's fields, so the sort
 * needs the field to EXIST while the table must not gain it. Promoting it to a
 * view item would typecheck and keep the sort — and would also add a row to the
 * column-visibility menu, a "Created At" line to the detail card's trailing
 * section (`columnInvisible` answers the LIST surface only), and a diff in
 * `src/__tests__/architecture/resource-field-sets.snapshot.json`.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { orderAPI, toListResult, ORDER_FILTERS } from '@/services';
import { ORDER_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';
import i18n from '@/i18n';
import { OrderCustomerSection } from './components/OrderCustomerSection';
import { OrderStructureTree } from './components/OrderStructureTree';
import { OrderDetailExtra } from './components/OrderDetailExtra';
import { OrderActionsToolbar } from './components/OrderActionsToolbar';
import type { OrderResourceRow } from './types';

// Exported as the SOT enum-label tables so sibling surfaces (e.g. the
// customer-detail recent-orders card, #1223) render the same localized
// financial/fulfillment labels instead of re-leaking the raw wire form.
export const paymentOptions: EnumOption[] = [
  { value: 'paid', label: 'order.enum.paid', color: 'success' },
  { value: 'pending', label: 'order.enum.pending', color: 'secondary' },
  { value: 'authorized', label: 'order.enum.authorized', color: 'secondary' },
  { value: 'partially_paid', label: 'order.enum.partiallyPaid', color: 'secondary' },
  { value: 'partially_refunded', label: 'order.enum.partiallyRefunded', color: 'destructive' },
  { value: 'refunded', label: 'order.enum.refunded', color: 'destructive' },
  { value: 'voided', label: 'order.enum.voided', color: 'destructive' },
];

export const fulfillmentOptions: EnumOption[] = [
  { value: 'fulfilled', label: 'order.enum.fulfilled', color: 'success' },
  { value: 'partial', label: 'order.enum.partial', color: 'secondary' },
  { value: 'unfulfilled', label: 'order.enum.unfulfilled', color: 'destructive' },
];

const f = createFieldConstructors<OrderResourceRow>();

export const orderModel = defineModel<OrderResourceRow>()({
  name: 'order',
  idField: 'id',
  // What an operator calls an order. `recName` has no consumer on the detail
  // surface yet — the header title travels on the host, see
  // `packages/resource-kit-controllers/react/bundle.ts` — so this is the value
  // Stage B resolves to.
  recName: 'name',
  // Which row of the backend's data-change ledger orders write to. Read by
  // `packages/resource-kit-ui/react/form/FormView.tsx::FormView` to decide whether the
  // record gets a change-history
  // tab at all; omitting it removes the tab without a trace.
  auditResourceType: 'orders',
  fields: {
    name: f.text({ label: 'order.field.name', widget: 'orderName' }),
    email: f.email({ label: 'order.field.email', widget: 'orderCustomerEmail' }),
    // No `currencyField`: see the file header.
    total_price: f.currency({ label: 'order.field.totalPrice' }),
    currency: f.text({ label: 'order.field.currency' }),
    financial_status: f.enum({
      label: 'order.field.financialStatus',
      options: paymentOptions,
    }),
    fulfillment_status: f.enum({
      // `type` + `options` stay intact so the list filter keeps offering the
      // same closed set of fulfillment states (filter semantics unchanged —
      // the query still targets the backend column verbatim). The widget reads
      // the SAME table off `field.options`, which is what keeps it free of an
      // import from this module.
      label: 'order.field.fulfillmentStatus',
      options: fulfillmentOptions,
      widget: 'orderFulfillmentStatus',
    }),
    line_items: f.array({ label: 'order.field.lineItems', widget: 'orderLineItems' }),
    // Synthetic aggregate badge (#1409) — no backing column on `Order` (the
    // read wire emits no task count); `filterable: false` keeps it out of the
    // QueryBuilder. The badge loads real jobs on hover.
    onfleetTaskCount: f.virtual.number({
      label: 'order.field.onfleetTaskCount',
      filterable: false,
      widget: 'orderTaskCount',
    }),
    shipping_address: f.json({
      label: 'order.field.shippingAddress',
      widget: 'orderShippingAddress',
    }),
    processed_at: f.datetime({ label: 'order.field.processedAt', widget: 'orderProcessedAt' }),
    // Delivery date — the COLUMN value renders the denormalized
    // `MIN(fulfillment_jobs.delivery_date)` from migration 0026 (canonical
    // "earliest delivery"). FILTERS, however, are rewritten on the backend
    // (services/orders.ts) into an `EXISTS` against `fulfillment_jobs` so a
    // query like `delivery_date=eq:2026-04-30` matches every order that has ANY
    // active sub-order on 4/30 — not just the orders whose earliest batch is
    // 4/30. This is the semantic operators want ("show me orders shipping that
    // day"); without it a multi-batch order whose earliest day is 4/27 would be
    // invisible to a 4/30 filter even though one of its sub-orders ships 4/30.
    // Render-vs-filter divergence is intentional.
    delivery_date: f.date({ label: 'order.field.deliveryDate', widget: 'orderDeliveryDate' }),
    cancelled_at: f.datetime({ label: 'order.field.cancelledAt' }),
    cancel_reason: f.text({ label: 'order.field.cancelReason' }),
    // Filter-only mirror (#1409): `closed_at` is a real backend `/api/orders`
    // filter (ORDER_FILTERS) but the read wire never projects it as a column,
    // so it is not a key of `Order` — declared virtual to keep the QueryBuilder
    // date filter while the detail cell renders empty.
    closed_at: f.virtual.datetime({ label: 'order.field.closedAt' }),
    note: f.text({ label: 'order.field.note' }),
    tags: f.text({ label: 'order.field.tags' }),
    shopify_id: f.id({ label: 'order.field.shopifyId' }),
    // Model-only: the sort key, never a column. See the file header.
    created_at: f.datetime({ label: 'order.field.createdAt' }),
  },
});

export const orderListView = defineListView(orderModel, {
  items: [
    { field: 'name' },
    { field: 'email' },
    { field: 'total_price' },
    { field: 'currency', columnInvisible: true },
    { field: 'financial_status' },
    { field: 'fulfillment_status' },
    { field: 'line_items' },
    { field: 'onfleetTaskCount' },
    { field: 'shipping_address' },
    { field: 'processed_at' },
    { field: 'delivery_date' },
    { field: 'cancelled_at', columnInvisible: true },
    { field: 'cancel_reason', columnInvisible: true },
    { field: 'closed_at', columnInvisible: true },
    { field: 'note', columnInvisible: true },
    { field: 'tags', columnInvisible: true },
    { field: 'shopify_id', columnInvisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
  capabilities: {
    // `/api/orders` really searches (name / email / order_number) — see
    // ORDER_SEARCH in filters.generated.ts (#1642), and
    // `services/__tests__/resource-searchable-parity.test.ts` holds orders in
    // the set of routes that DO implement search.
    search: true,
    // Selection is left to the source default (on, for a page-level list).
    // This used to be an explicit `false` reasoned as "there is no bulk
    // command behind one" — true then, but no longer the question: the export
    // dropdown's "Export selected" scope gives the selection a job of its own.
  },
});

/**
 * Read-only order book: the detail drawer shows the same columns, grouped, and
 * there is no write path — hence no `create` / `update` / `delete` on the model
 * api and no `required` anywhere.
 */
export const orderFormView = defineFormView(orderModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'summary',
      label: 'order.group.summary',
      items: [
        { field: 'name' },
        { field: 'email' },
        { field: 'total_price' },
        { field: 'currency' },
        { field: 'financial_status' },
        { field: 'fulfillment_status' },
        { field: 'onfleetTaskCount' },
        // Was a `shipping` group of its own until #1922. One field is not
        // worth a rail trigger, and the delivery address reads as part of
        // "what is this order" — it sits at the tail because it renders as a
        // block rather than a line.
        { field: 'shipping_address' },
      ],
    },
    {
      kind: 'fields',
      id: 'items',
      label: 'order.group.items',
      items: [{ field: 'line_items' }],
    },
    // Troubleshooting-tier fields (#1204): the deeper layers, kept off the
    // first screen so it shows only "what is this order / how is it being
    // fulfilled". All fields stay one click away. The payment group (#1409) is
    // gone — gateway / discount_codes / tax_lines were off-wire columns the
    // read never emitted, now deleted.
    //
    // #1922 merged the former `timeline` / `notes` / `meta` groups into this
    // one. See the file header for why the flags stay on the merged group.
    // `delivery_date` is deliberately NOT promoted to `summary`: the
    // authoritative delivery-date surface is the `fulfillment` tab's job tree
    // (an order can ship on several days), and this column is only the
    // earliest-batch projection of it.
    {
      kind: 'fields',
      id: 'more',
      label: 'order.group.more',
      items: [
        { field: 'processed_at' },
        { field: 'delivery_date' },
        { field: 'cancelled_at' },
        { field: 'cancel_reason' },
        { field: 'closed_at' },
        { field: 'note' },
        { field: 'tags' },
        { field: 'shopify_id' },
      ],
      collapsible: true,
      defaultCollapsed: true,
    },
    // The remaining tabs, each a whole layer of the order (#1185, #1204 IA,
    // moved onto the rail by #1804):
    //   - customer:    who this order is for, next to the shipping fields.
    //   - fulfillment: how it is being fulfilled — the delivery-job tree.
    //   - audit:       what changed, and who changed it.
    //   - trace:       how the data got here — the admin-only event chain.
    //
    // The trace shared the fulfillment tab until #1921, and the reason given
    // was that the tree beside it kept the trace's admin gate from leaving
    // everyone else an empty tab. That reason is retired, not merely
    // outweighed: `gated: true` + `host.detail.tabGates` below remove the
    // TRIGGER for a non-admin, so the gate no longer needs a chaperone and the
    // pairing was costing every admin a scroll past the tree.
    {
      kind: 'custom',
      id: 'customer',
      label: 'order.customer.title',
      render: function OrderCustomerTab({ record }) {
        return <OrderCustomerSection order={record} />;
      },
    },
    {
      kind: 'custom',
      id: 'fulfillment',
      label: 'order.structure.title',
      render: function OrderFulfillmentTab({ record }) {
        return <OrderStructureTree order={record} />;
      },
    },
    // Every audit-covered resource declares its audit node; orders places it
    // before the gated trace tab.
    { kind: 'audit', id: 'audit', label: 'resource.changeHistory' },
    {
      kind: 'custom',
      id: 'trace',
      label: 'order.trace.sectionTitle',
      gated: true,
      render: function OrderTraceTab({ record }) {
        return <OrderDetailExtra order={record} />;
      },
    },
  ],
});

export const orderResource: ResourceBundle<OrderResourceRow, typeof orderModel.fields> = {
  model: orderModel,
  list: orderListView,
  form: orderFormView,
  host: {
    api: {
      // The `/api` `{ items, total, page, size }` page is renamed to the
      // controllers' `ListResult` at this boundary (#2167 PR-2); `size` is the
      // kernel's key and stops here.
      list: async (params) => toListResult(await orderAPI.getOrders(params)),
      // Flatten the detail-only order projection, restore the canonical
      // top-level line-items array, and retain the embedded customer for the
      // order detail section. List rows remain the untouched OrderSummary.
      get: async (id) => {
        const detail = await orderAPI.getOrder(id);
        return {
          ...detail.order,
          line_items: detail.line_items,
          customer: detail.customer,
        } satisfies OrderResourceRow;
      },
    },
    label: 'order.label',
    slug: 'orders',
    listTitle: 'order.listTitle',
    listDescription: 'order.listDescription',
    // `orders:list` folded into `orders:read` (#943 catalog unify).
    permission: { list: PERMISSIONS.ORDERS_READ, read: PERMISSIONS.ORDERS_READ },
    // Backend filterable whitelist (#contains-filter): the QueryBuilder trims
    // its columns/ops to this, and toApiListParams forwards only these.
    filterable: ORDER_FILTERS,
    sortable: ORDER_SORTS,
    searchPlaceholder: 'order.searchPlaceholder',
    staleTime: 2 * 60 * 1000,
    detail: {
      title: (o) => o.name,
      subtitle: (o) => i18n.t('order.detail.subtitle', { number: o.order_number }),
      // #1291 — payment status badge next to the order title in the detail
      // header.
      // `packages/resource-kit-ui/react/resource/ResourceDetailPanel.tsx::ResourceDetailPanel`
      // looks the key up in `definition.fields`; no cell widget can reach
      // that slot.
      statusField: 'financial_status',
      // The order's commands (Re-fetch) live in the detail header, on screen
      // whichever tab the rail shows — a command must never cost a click to
      // find (#1804).
      headerActions: (o) => <OrderActionsToolbar order={o} />,
      // The verdict for the one gated tab this form declares (#1921). Orders
      // is the only resource with a trace, so this map is one entry, and it
      // mirrors the backend route rather than inventing a frontend-only rule.
      // A tab id absent from here — or this callback absent entirely — hides
      // the tab.
      //
      // That backend route is `debug:read` since #1964, and `isAdmin` stays in
      // the disjunction because `requirePermission` short-circuits on the
      // admin role: an admin reaches the trace without holding the key, so a
      // grant-only check here would hide a tab the API would have served.
      tabGates: ({ isAdmin, hasGrant }) => ({
        trace: isAdmin || hasGrant(PERMISSIONS.DEBUG_READ),
      }),
    },
  },
};
